
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    if (location.pathname !== '/') {
      e.preventDefault();
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      e.preventDefault();
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-stone-700 text-white py-16" role="contentinfo">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* About */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://public.readdy.ai/ai/img_res/dddcdb8a-4f07-441e-8e3f-1ae6a286ee38.png"
                alt="Yoshino Village Network Logo"
                className="h-10 w-auto"
              />
              <h3 className="text-xl font-bold">Yoshino Village Network</h3>
            </div>
            <p className="text-white/80 text-sm leading-relaxed">
              {i18n.language === 'ja' 
                ? '吉野、気になる。でも何から？決まってなくてOK。挑戦の現場への入口です。'
                : 'Curious about Yoshino? But where to start? It\'s okay not to know. This is the gateway to real challenges.'}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-lg font-bold mb-4">
              {i18n.language === 'ja' ? 'ナビゲーション' : 'Navigation'}
            </h4>
            <nav aria-label={i18n.language === 'ja' ? 'フッターナビゲーション' : 'Footer navigation'}>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="#about" 
                    onClick={(e) => handleNavClick(e, 'about')}
                    className="text-white/80 hover:text-white transition-colors underline-offset-4 hover:underline"
                  >
                    {i18n.language === 'ja' ? '我々について' : 'About Us'}
                  </a>
                </li>
                <li>
                  <a 
                    href="#steps" 
                    onClick={(e) => handleNavClick(e, 'steps')}
                    className="text-white/80 hover:text-white transition-colors underline-offset-4 hover:underline"
                  >
                    {i18n.language === 'ja' ? 'はじめての方へ' : 'For Beginners'}
                  </a>
                </li>
                <li>
                  <a 
                    href="#challenges" 
                    onClick={(e) => handleNavClick(e, 'challenges')}
                    className="text-white/80 hover:text-white transition-colors underline-offset-4 hover:underline"
                  >
                    {i18n.language === 'ja' ? 'いま動いている挑戦' : 'Current Challenges'}
                  </a>
                </li>
              </ul>
            </nav>
          </div>

          {/* Social Media */}
          <div>
            <h4 className="text-lg font-bold mb-4">
              {i18n.language === 'ja' ? 'ソーシャルメディア' : 'Social Media'}
            </h4>
            <nav aria-label={i18n.language === 'ja' ? 'ソーシャルメディアリンク' : 'Social media links'}>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="https://www.instagram.com/yoshino_village_project/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition-colors flex items-center gap-2"
                    aria-label={i18n.language === 'ja' ? 'InstagramでYoshino Village Networkをフォロー（新しいタブで開く）' : 'Follow Yoshino Village Network on Instagram (opens in new tab)'}
                  >
                    <i className="ri-instagram-line text-lg w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                    Instagram
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.youtube.com/@YoshinoNowVoice"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition-colors flex items-center gap-2"
                    aria-label={i18n.language === 'ja' ? 'YouTubeでYoshino Now Voiceを視聴（新しいタブで開く）' : 'Watch Yoshino Now Voice on YouTube (opens in new tab)'}
                  >
                    <i className="ri-youtube-line text-lg w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                    YouTube
                  </a>
                </li>
                <li>
                  <a
                    href="https://note.com/yoshinovillage"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition-colors flex items-center gap-2"
                    aria-label={i18n.language === 'ja' ? 'noteで記事を読む（新しいタブで開く）' : 'Read articles on note (opens in new tab)'}
                  >
                    <i className="ri-article-line text-lg w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                    note
                  </a>
                </li>
                <li className="pt-2">
                  <div className="flex items-center gap-4">
                    <a
                      href="https://open.spotify.com/show/3TSoXSt857tDf0F7g9PVJs?si=8PYs2qWjRjmpXDDG3G_T8g"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/80 hover:text-white transition-colors"
                      aria-label={i18n.language === 'ja' ? 'SpotifyでPodcastを聴く（新しいタブで開く）' : 'Listen to Podcast on Spotify (opens in new tab)'}
                    >
                      <img 
                        src="https://storage.googleapis.com/pr-newsroom-wp/1/2023/05/Spotify_Primary_Logo_RGB_White.png" 
                        alt="Spotify" 
                        className="h-6 w-auto"
                      />
                    </a>
                    <a
                      href="https://podcasts.apple.com/us/podcast/yoshino-now-voice/id1860578983"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/80 hover:text-white transition-colors"
                      aria-label={i18n.language === 'ja' ? 'Apple PodcastsでPodcastを聴く（新しいタブで開く）' : 'Listen to Podcast on Apple Podcasts (opens in new tab)'}
                    >
                      <img 
                        src="https://www.apple.com/v/apple-podcasts/b/images/overview/hero_icon__c135x5gz14mu_large.png" 
                        alt="Apple Podcasts" 
                        className="h-6 w-auto"
                      />
                    </a>
                    <a
                      href="https://music.amazon.co.jp/podcasts/e7713530-cf1e-4782-8e07-8f0d80f18de3/yoshino-now-voice"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/80 hover:text-white transition-colors"
                      aria-label={i18n.language === 'ja' ? 'Amazon MusicでPodcastを聴く（新しいタブで開く）' : 'Listen to Podcast on Amazon Music (opens in new tab)'}
                    >
                      <img 
                        src="https://m.media-amazon.com/images/G/01/digital/music/player/web/EQ_accent.png" 
                        alt="Amazon Music" 
                        className="h-6 w-auto brightness-0 invert"
                      />
                    </a>
                  </div>
                </li>
              </ul>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-4">
              {i18n.language === 'ja' ? 'お問い合わせ' : 'Contact'}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a 
                  href="/consultation"
                  className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-lg font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-chat-3-line text-lg w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                  {i18n.language === 'ja' ? '吉野なんでも相談所' : 'Yoshino Consultation'}
                </a>
              </li>
              <li className="text-white/80 text-sm mt-4">
                {i18n.language === 'ja' 
                  ? '返信目安:2-3営業日'
                  : 'Reply time: 2-3 business days'}<br />
                <span className="text-white/60">
                  {i18n.language === 'ja' 
                    ? '(速さより信頼を大切に)'
                    : '(We value trust over speed)'}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} Yoshino Village Network. All rights reserved.
            </p>
            <a 
              href="/privacy" 
              className="text-white/60 hover:text-white transition-colors underline-offset-4 hover:underline text-sm"
            >
              {i18n.language === 'ja' ? 'プライバシーポリシー' : 'Privacy Policy'}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
